import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AutoRefreshCountdown from '../common/AutoRefreshCountdown';

interface OperatorRow {
  user_id: number | null;
  user_name: string;
  quantity: number;
  kg: number;
  item_count: number;
  first_entry_at: string | null;
  last_entry_at: string | null;
  items: Array<{
    production_item_id: number;
    indeks_kat: string | null;
    nazwa_oryg: string | null;
    linia: string | null;
    quantity: number;
    kg: number;
  }>;
}

interface ShiftBucket {
  operators: OperatorRow[];
  total: number;
  total_kg: number;
  /** Shift window the data is taken from (shift 2 crosses midnight). */
  from: string;
  to: string;
  /** Timestamps of the first and last entry actually recorded within that window. */
  first_entry_at: string | null;
  last_entry_at: string | null;
}

interface ShiftOutputResponse {
  date: string;
  day_from: string;
  day_to: string;
  shifts: Record<'zmiana_1' | 'zmiana_2', ShiftBucket>;
}

const emptyBucket = (): ShiftBucket => ({
  operators: [], total: 0, total_kg: 0, from: '', to: '', first_entry_at: null, last_entry_at: null,
});

const EMPTY: ShiftOutputResponse = {
  date: '',
  day_from: '',
  day_to: '',
  shifts: { zmiana_1: emptyBucket(), zmiana_2: emptyBucket() },
};

const formatKg = (kg: number) => `${kg.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;

/** "18.08, 19:00" — day is shown too, because the night shift starts on the previous date. */
const formatMoment = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const formatRange = (from: string | null, to: string | null) => `${formatMoment(from)} – ${formatMoment(to)}`;

const todayISO = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

/**
 * "Ile kto wyprodukował na danej zmianie" — dzienny wykaz wydajności operatorów,
 * rozbity na zmianę 1 i 2. Dane pochodzą z historii wpisów ilości (production_history).
 */
const ShiftOutputReport: React.FC = () => {
  const { t } = useLanguage();
  const [date, setDate] = useState<string>(todayISO());
  const [data, setData] = useState<ShiftOutputResponse>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<ShiftOutputResponse>('/admin/shift-output', { params: { date } });
      setData(res.data ?? EMPTY);
    } catch {
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const shiftBlocks: Array<{ key: 'zmiana_1' | 'zmiana_2'; label: string; accent: string }> = [
    { key: 'zmiana_1', label: t('production.shift1'), accent: '#667eea' },
    { key: 'zmiana_2', label: t('production.shift2'), accent: '#0ea5e9' },
  ];

  const grandTotal = data.shifts.zmiana_1.total + data.shifts.zmiana_2.total;
  const grandTotalKg = data.shifts.zmiana_1.total_kg + data.shifts.zmiana_2.total_kg;

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>
            📊 {t('shiftOutput.title')}
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>
            {t('shiftOutput.subtitle')}
          </p>
        </div>
        <AutoRefreshCountdown intervalSeconds={600} onRefresh={fetchReport} />
      </div>

      <div style={{
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <label style={{ fontSize: 14, color: '#475569', fontWeight: 600 }}>{t('shiftOutput.dateLabel')}</label>
        <input
          type="date"
          value={date}
          max={todayISO()}
          onChange={(e) => setDate(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 }}
        />
        <button
          onClick={() => setDate(todayISO())}
          style={{
            padding: '6px 16px', background: '#667eea', color: '#fff', border: 'none',
            borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}
        >
          {t('shiftOutput.today')}
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 14, color: '#475569' }}>
          {t('shiftOutput.dayTotal')}{' '}
          <strong style={{ fontSize: 18, color: '#1e293b' }}>{grandTotal.toLocaleString('pl-PL')}</strong>
          <span style={{ color: '#94a3b8' }}> · </span>
          <strong style={{ fontSize: 18, color: '#667eea' }}>{formatKg(grandTotalKg)}</strong>
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>{t('common.loading')}</div>
      ) : (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {shiftBlocks.map(({ key, label, accent }) => {
            const bucket = data.shifts[key];
            return (
              <div
                key={key}
                style={{
                  flex: 1, minWidth: 340, background: '#fff', border: '1px solid #e2e8f0',
                  borderTop: `3px solid ${accent}`, borderRadius: 10, overflow: 'hidden',
                }}
              >
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{label}</h3>
                    <span style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: accent }}>
                        {bucket.total.toLocaleString('pl-PL')}
                      </span>
                      <span style={{ fontSize: 13, color: '#64748b' }}> {t('shiftOutput.pieces')}</span>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{formatKg(bucket.total_kg)}</div>
                    </span>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>{t('shiftOutput.range')}</span>{' '}
                      {formatRange(bucket.from, bucket.to)}
                    </div>
                  </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={thStyle}>{t('shiftOutput.operator')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('shiftOutput.quantity')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('shiftOutput.kg')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('shiftOutput.itemCount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bucket.operators.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ padding: 28, textAlign: 'center', color: '#94a3b8' }}>
                          {t('shiftOutput.noData')}
                        </td>
                      </tr>
                    ) : (
                      bucket.operators.map((op) => {
                        const rowKey = `${key}-${op.user_id ?? 'unknown'}`;
                        const open = expanded === rowKey;
                        return (
                          <React.Fragment key={rowKey}>
                            <tr
                              onClick={() => setExpanded(open ? null : rowKey)}
                              style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer' }}
                            >
                              <td style={tdStyle}>
                                <span style={{ color: '#94a3b8', marginRight: 6 }}>{open ? '▾' : '▸'}</span>
                                {op.user_name}
                                <div style={{ marginLeft: 18, fontSize: 11, color: '#94a3b8' }}>
                                  {formatRange(op.first_entry_at, op.last_entry_at)}
                                </div>
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: op.quantity < 0 ? '#dc3545' : '#1e293b' }}>
                                {op.quantity.toLocaleString('pl-PL')}
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: op.kg < 0 ? '#dc3545' : '#667eea' }}>
                                {formatKg(op.kg)}
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b' }}>{op.item_count}</td>
                            </tr>
                            {open && (
                              <tr>
                                <td colSpan={4} style={{ padding: '8px 20px 14px', background: '#f8fafc' }}>
                                  <ul style={{ margin: 0, paddingLeft: 20, color: '#475569', fontSize: 12, lineHeight: 1.7 }}>
                                    {op.items.map((it) => (
                                      <li key={it.production_item_id}>
                                        <strong>{it.indeks_kat || it.nazwa_oryg || `#${it.production_item_id}`}</strong>
                                        {it.linia ? <span style={{ color: '#94a3b8' }}> · {it.linia}</span> : null}
                                        <span style={{ color: '#64748b' }}>
                                          {' · '}{it.quantity.toLocaleString('pl-PL')} {t('shiftOutput.pieces')} · {formatKg(it.kg)}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ marginTop: 20, fontSize: 12, color: '#94a3b8' }}>
        {t('shiftOutput.methodHint')}
      </p>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '10px 20px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: '#64748b',
  borderBottom: '1px solid #e2e8f0',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 20px',
  color: '#1e293b',
};

export default ShiftOutputReport;
